/**
 * Google Tag Manager — minimal client-side integration.
 *
 *  - The container ID is stored in PlatformSettings (SuperAdmin sets it
 *    via /admin/tools/settings) and exposed by the public
 *    `/api/platformSettings/public` endpoint, so we can load GTM on
 *    anonymous pages too.
 *  - `loadGtm` is idempotent : calling it twice with the same container
 *    is a no-op. It builds the same dataLayer + script + iframe pair
 *    Google ships in their docs.
 *  - `pushEvent` is safe to call before GTM has loaded — events queue
 *    up on the dataLayer and are flushed once the script kicks in.
 */

declare global {
  interface Window {
    dataLayer?: Array<Record<string, any>>;
  }
}

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
let loadedContainerId: string | null = null;

/**
 * Pages where we inject GTM. Authenticated routes are intentionally
 * excluded for V1 to keep RGPD scope tight (no behavioural tracking on
 * logged-in user actions until we explicitly opt in).
 */
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/register',
  '/forgotpassword',
  '/forgotPassword',
  '/resetpassword',
  '/resetPassword',
  '/cgu',
  '/logout',
];
const PUBLIC_PREFIXES = ['/cgu/', '/preview-shell', '/preview-ui'];

export function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

export function ensureDataLayer(): Array<Record<string, any>> {
  if (typeof window === 'undefined') return [];
  if (!window.dataLayer) window.dataLayer = [];
  return window.dataLayer;
}

export function loadGtm(containerId: string | null | undefined): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const id = (containerId ?? '').trim().toUpperCase();
  if (!id || !GTM_ID_PATTERN.test(id)) return;
  if (loadedContainerId === id) return;
  if (loadedContainerId && loadedContainerId !== id) {
    // Switching container is rare and would require a full reload to
    // unload Google's globals cleanly — bail loudly instead.
    // eslint-disable-next-line no-console
    console.warn(
      `[gtm] container ID changed (${loadedContainerId} -> ${id}) but a previous one is already loaded; reload the page to apply.`,
    );
    return;
  }

  const dl = ensureDataLayer();
  dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  // Noscript iframe fallback — minor SEO/parity bonus.
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  const noscript = document.createElement('noscript');
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  loadedContainerId = id;
}

/**
 * Push a custom event to the dataLayer. Call from buttons / form
 * submits / etc. — works whether GTM has loaded or not (queue).
 */
export function pushEvent(event: string, props: Record<string, any> = {}): void {
  if (typeof window === 'undefined') return;
  ensureDataLayer().push({ event, ...props });
}
